/**
 * Every write the Tax Profile page makes to one of the profile's values: typing a new value,
 * accepting one of the values the sources gave or keeping the approved one (markReviewed, which
 * sends the review route), accepting many at once (markManyReviewed), and putting a stored row back
 * into the column, which is the Undo of an edit.
 *
 * Expects the client id and the id of the legal entity the page is showing (`shownEntityId` in
 * TaxProfile.tsx), which every request below sends as `entityId`. A client with more than one legal
 * entity has every write that names none refused by the api with 400, so the id is added here once
 * rather than by each caller. null sends none, which the api accepts only for a client with exactly
 * one legal entity.
 *
 * Returns `notices`, what each row says after an act, keyed by noticeKey below; `isWriting`, true
 * while at least one write is in flight (a count, so the first of two overlapping writes to finish
 * does not free the buttons while the other is still going), which the rows' buttons wait on; and the six writers editValue,
 * applyStoredRow, markReviewed, markManyReviewed, reviewListRow and removeListRow. reviewListRow is
 * the Confirm review button of a row of the owners, officers, subsidiaries or state registrations
 * tables; removeListRow is the Remove owner / Remove officer button of the person window.
 *
 * Every write carries the row the page displayed as the value's source (liveRowId) and the ids of
 * the open assertions the page displayed for it (consideredRowIds), which the api closes. Each act
 * a person can take back leaves a notice on its row for NOTICE_MS with the Undo that reverses it.
 * A write that lands on a value somebody else has changed is answered with the value as it stands
 * now, and the page then offers "Keep theirs", which reads the profile again and drops what was
 * typed, and "Overwrite", which sends the same write against the row that is live now. Either
 * button closes the toast, and both are disabled while a write is in flight. The review
 * route answers per value rather than with a status, so its refusal is read off that item's result;
 * the other three routes answer 409, which utils/api.ts carries on the error it throws.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { mutate } from 'swr';
import {
  TaxProfileFieldDefinition,
  TaxProfileListColumnDefinition,
  taxProfileFieldByKey,
  taxProfileListColumnByKey,
} from '@shared/constants/taxProfileFields';
import type {
  ReviewTaxProfileFieldsRequest,
  TaxProfilePersonTable,
  ReviewTaxProfileFieldsResponse,
  TaxProfileFieldValue,
  TaxProfileFieldWriteResponse,
  TaxProfileWriteScope,
} from '@shared/types/taxProfile';
import {
  alertErrorMessage,
  alertMessageSuccess,
  alertWarningMessageNode,
} from '@/utils/alerts';
import useTaxProfile, {
  conflictFromError,
  matchesProfileKey,
  refreshAfterFieldWrite,
} from '@/hooks/useTaxProfile';
import { formatFieldValue } from '@/pages/TaxProfile/format';
import { newestWaiting, shownValue } from '@/pages/TaxProfile/fieldState';
import {
  ACCEPTED_NOTICE,
  CONFLICT_WORDS,
  KEPT_NOTICE,
  SAVED_NOTICE,
  acceptedCountNotice,
  REMOVE_PERSON_WORDS,
  bulkConflictSentence,
  conflictSentence,
} from '@/pages/TaxProfile/wording';
import type { FieldRowNotice } from '@/pages/TaxProfile/components/FieldRow';

/** How long a row keeps the notice of what was just done, and its Undo. */
export const NOTICE_MS = 8000;

/**
 * What a row's notice is filed under: the field's catalog key and the tax year the notice is about.
 *
 * The year is part of the key because a per-year value is a different value in each year. Keyed by
 * the catalog key alone, a notice raised for 2024 stayed on the row after the year switch moved to
 * 2023, and its Undo still wrote the year it had been created for.
 */
export const noticeKey = (
  key: string,
  taxYear: number | null,
  recordId: number | null = null
): string =>
  recordId == null
    ? `${key}:${taxYear ?? ''}`
    : `${key}:${taxYear ?? ''}:${recordId}`;

/**
 * What a write says a value is: a field of the catalog, or a column of one of the list tables, which
 * a write also names the row of (FieldWriteTarget.recordId).
 */
export type WritableDefinition =
  | TaxProfileFieldDefinition
  | TaxProfileListColumnDefinition;

/** One value a write is about: which field, which tax year, and what the page displayed for it. */
export type FieldWriteTarget = {
  definition: WritableDefinition;
  taxYear: number | null;
  /**
   * The owner, officer, subsidiary or state registration a list-table column is on; left out for a
   * field of the catalog. Also part of the noticeKey the write's notice is filed under.
   */
  recordId?: number;
  scope: TaxProfileWriteScope;
  /**
   * The candidate markReviewed accepts: the id of the first source of one waiting value. Left out,
   * markReviewed keeps the approved value and closes the candidates the page showed.
   */
  acceptRowId?: number;
};

/**
 * The write target of a value the profile response carries.
 *
 * Expects the value, its catalog entry, and `shownTaxYear`, the year the page's year switch has on.
 *
 * Returns the catalog entry, the tax year to write under, the scope the api sent with the value
 * (the approved row's id and the ids of the open candidates the page shows), and, on a value nobody
 * has approved, the first source of the newest waiting value, which the row's Accept button and
 * the accept-all button accept. ValuePicker replaces it with the waiting value a person picked.
 *
 * A per-year value falls back to `shownTaxYear` when the response carried no year for it. That
 * happens on a client with no tax_profile_yearly_values row at all: the api answers with no year,
 * every per-year line carries null, and a write with no year on it is refused. The switch always
 * offers the current tax year and the one before it, so there is a year to write the first value
 * under.
 */
export const targetFromField = (
  field: TaxProfileFieldValue,
  definition: TaxProfileFieldDefinition,
  shownTaxYear: number | null = null
): FieldWriteTarget => ({
  definition,
  taxYear: definition.yearly ? field.taxYear ?? shownTaxYear : field.taxYear,
  scope: {
    liveRowId: field.liveRowId,
    consideredRowIds: field.consideredRowIds,
  },
  ...(field.status === 'proposed' && newestWaiting(field)
    ? { acceptRowId: newestWaiting(field)?.sources[0]?.rowId }
    : {}),
});

/** The label the catalog gives a field, for a toast that names several fields at once. */
const labelOf = (key: string) =>
  (taxProfileFieldByKey(key) ?? taxProfileListColumnByKey(key))?.label ?? key;

/** The row a write names, spread into a request body; nothing for a field of the catalog. */
const recordIdOf = (recordId: number | undefined) =>
  recordId != null ? { recordId } : {};

/**
 * The items of a request to the review route: one per target, naming the target's candidate as
 * rowId where it has one (accept) and none where it has none (keep the approved value), and the
 * target's row for a list-table column.
 */
const reviewItemsOf = (
  targets: FieldWriteTarget[]
): ReviewTaxProfileFieldsRequest['items'] =>
  targets.map(({ definition, taxYear, scope, acceptRowId, recordId }) => ({
    ...scope,
    key: definition.key,
    taxYear,
    ...recordIdOf(recordId),
    ...(acceptRowId != null ? { rowId: acceptRowId } : {}),
  }));

/** One cell a row's Confirm review types a value into, through the edit route. */
export type TypedCellWrite = {
  target: FieldWriteTarget;
  value: string | number | boolean | null;
};

/**
 * One value of a Confirm review that the api refused, with the api's sentence, and the list row and
 * tax year the value is on, which tell apart two values of one key on a document's summary screen.
 */
export type RowWriteFailure = {
  key: string;
  error: string;
  recordId?: number;
  taxYear?: number | null;
};

/**
 * How many writes are in flight, readable outside the page's own render: the ConflictToast below is
 * drawn by react-toastify's ToastContainer, which a prop of the page never reaches, so the toast
 * subscribes to this count with useSyncExternalStore and the hook reads it the same way.
 */
type WritesInFlight = {
  count: () => number;
  subscribe: (listener: () => void) => () => void;
  /** Adds 1 when a write starts and -1 when it settles, and tells every subscriber. */
  add: (change: 1 | -1) => void;
};

const createWritesInFlight = (): WritesInFlight => {
  let count = 0;
  const listeners = new Set<() => void>();
  return {
    count: () => count,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    add: (change) => {
      count += change;
      listeners.forEach((listener) => listener());
    },
  };
};

/**
 * The body of the toast a write gets when somebody else changed the value first.
 *
 * Expects the sentence naming the value as it stands now, the two callbacks the buttons call,
 * `closeToast`, which react-toastify hands to a toast whose content is a function, and the hook's
 * count of writes in flight.
 *
 * Renders that sentence over a "Keep theirs" button and an "Overwrite" button. A click on either
 * closes the toast before calling its callback, so Overwrite cannot be sent twice and Keep theirs
 * cannot be followed by Overwrite. Both are disabled while any write is in flight.
 */
function ConflictToast({
  sentence,
  onKeepTheirs,
  onOverwrite,
  closeToast,
  writesInFlight,
}: {
  sentence: string;
  onKeepTheirs: () => void;
  onOverwrite: () => void;
  closeToast?: () => void;
  writesInFlight: WritesInFlight;
}) {
  const busy =
    useSyncExternalStore(writesInFlight.subscribe, writesInFlight.count) > 0;
  return (
    <div className='flex flex-col gap-2'>
      <span>{sentence}</span>
      <span className='flex gap-2'>
        <button
          type='button'
          className='btn btn-xs'
          disabled={busy}
          onClick={() => {
            closeToast?.();
            onKeepTheirs();
          }}
        >
          {CONFLICT_WORDS.keepTheirs}
        </button>
        <button
          type='button'
          className='btn btn-xs btn-accent'
          disabled={busy}
          onClick={() => {
            closeToast?.();
            onOverwrite();
          }}
        >
          {CONFLICT_WORDS.overwrite}
        </button>
      </span>
    </div>
  );
}

/** What the hook hands back, which the page and its tables call. */
export type ProfileFieldWrites = {
  notices: Record<string, FieldRowNotice>;
  isWriting: boolean;
  editValue: (
    target: FieldWriteTarget,
    value: string | number | boolean | null
  ) => void;
  applyStoredRow: (target: FieldWriteTarget, rowId: number) => void;
  markReviewed: (target: FieldWriteTarget) => void;
  markManyReviewed: (targets: FieldWriteTarget[]) => Promise<void>;
  reviewListRow: (
    decided: FieldWriteTarget[],
    typed: TypedCellWrite[],
    entityId?: number | null
  ) => Promise<RowWriteFailure[]>;
  removeListRow: (
    table: TaxProfilePersonTable,
    recordId: number
  ) => Promise<string | null>;
};

export default function useProfileFieldWrites(
  clientId: number,
  shownEntityId: number | null
): ProfileFieldWrites {
  const { editField, review, applyRow, removePerson } = useTaxProfile();
  /** The `entityId` every request carries: the shown legal entity, or nothing when there is none. */
  const shownEntity = shownEntityId != null ? { entityId: shownEntityId } : {};
  /** What each row says after an act a person can take back, keyed by noticeKey above. */
  const [notices, setNotices] = useState<Record<string, FieldRowNotice>>({});
  /** The writes in flight, as a count: the page's buttons wait while it is above 0. */
  const writesInFlight = useRef(createWritesInFlight()).current;
  const isWriting =
    useSyncExternalStore(writesInFlight.subscribe, writesInFlight.count) > 0;
  /** Runs one write while it is counted in writesInFlight. */
  const counted = async <T,>(write: () => Promise<T>): Promise<T> => {
    writesInFlight.add(1);
    try {
      return await write();
    } finally {
      writesInFlight.add(-1);
    }
  };
  const noticeTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const timers = noticeTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const dropNotice = (key: string) => {
    const timer = noticeTimers.current.get(key);
    if (timer) clearTimeout(timer);
    noticeTimers.current.delete(key);
    setNotices((kept) => {
      if (!(key in kept)) return kept;
      const rest = { ...kept };
      delete rest[key];
      return rest;
    });
  };

  /** Puts a notice on a row for NOTICE_MS. Leave `undo` out and the row shows the message alone. */
  const showNotice = (key: string, message: string, undo?: () => void) => {
    const running = noticeTimers.current.get(key);
    if (running) clearTimeout(running);
    setNotices((kept) => ({
      ...kept,
      [key]: {
        message,
        ...(undo
          ? {
              onUndo: () => {
                dropNotice(key);
                undo();
              },
            }
          : {}),
      },
    }));
    noticeTimers.current.set(
      key,
      setTimeout(() => dropNotice(key), NOTICE_MS)
    );
  };

  /** Reads the profile again, so the page shows the value the other person wrote. */
  const keepTheirs = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    mutate(matchesProfileKey(clientId));
  };

  const offerConflictChoice = (
    definition: WritableDefinition,
    current: TaxProfileFieldValue,
    overwrite: (liveRowId: number | null) => void
  ) => {
    alertWarningMessageNode(({ closeToast }) => (
      <ConflictToast
        sentence={conflictSentence(
          formatFieldValue(shownValue(current), definition)
        )}
        onKeepTheirs={keepTheirs}
        onOverwrite={() => overwrite(current.liveRowId)}
        closeToast={closeToast}
        writesInFlight={writesInFlight}
      />
    ));
  };

  /**
   * Sends one write and counts it in writesInFlight while it is in flight.
   *
   * Expects the catalog entry, the request to send, what to do with the answer, and `overwrite`,
   * which sends the same write again against a row id the api named.
   *
   * A 409 raises the ConflictToast above; any other refusal raises a plain error toast.
   */
  const send = async <T,>(
    definition: WritableDefinition,
    request: () => Promise<T>,
    onWritten: (answer: T) => void,
    overwrite: (liveRowId: number | null) => void
  ) => {
    await counted(async () => {
      try {
        const answer = await request();
        onWritten(answer);
      } catch (error) {
        const conflict = conflictFromError(error);
        if (conflict) {
          offerConflictChoice(definition, conflict.current, overwrite);
          return;
        }
        alertErrorMessage(
          (error as Error).message || 'The value could not be saved'
        );
      }
    });
  };

  /** Puts a stored row of the same field back into the column: the row an edit superseded. */
  const applyStoredRow = (
    target: FieldWriteTarget,
    rowId: number,
    liveRowId: number | null = target.scope.liveRowId
  ) => {
    const { definition, taxYear, scope, recordId } = target;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    send(
      definition,
      () =>
        applyRow(clientId, definition.key, {
          ...scope,
          ...shownEntity,
          liveRowId,
          taxYear,
          ...recordIdOf(recordId),
          rowId,
        }),
      () => dropNotice(noticeKey(definition.key, taxYear, recordId ?? null)),
      (currentRowId) => applyStoredRow(target, rowId, currentRowId)
    );
  };

  /**
   * Writes a value a person typed, and puts an Undo on the row for NOTICE_MS.
   *
   * The Undo puts the row this edit superseded back in the column (previousRowId in the answer). A
   * value typed into a column that held none has no such row: previousRowId is null, and the Undo
   * writes the column empty instead, through the same edit route with no value. Both write against
   * the row this edit created, which is the row the column holds now.
   */
  const editValue = (
    target: FieldWriteTarget,
    value: string | number | boolean | null,
    liveRowId: number | null = target.scope.liveRowId
  ) => {
    const { definition, taxYear, scope, recordId } = target;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    send(
      definition,
      () =>
        editField(clientId, definition.key, {
          ...scope,
          ...shownEntity,
          liveRowId,
          taxYear,
          ...recordIdOf(recordId),
          value,
        }),
      (answer: TaxProfileFieldWriteResponse) => {
        const { previousRowId } = answer;
        const written: FieldWriteTarget = {
          definition,
          taxYear,
          ...recordIdOf(recordId),
          scope: {
            liveRowId: answer.field.liveRowId,
            consideredRowIds: [],
          },
        };
        showNotice(
          noticeKey(definition.key, taxYear, recordId ?? null),
          SAVED_NOTICE,
          () => {
            if (previousRowId === null) {
              editValue(written, null);
              return;
            }
            applyStoredRow(written, previousRowId);
          }
        );
      },
      (currentRowId) => editValue(target, value, currentRowId)
    );
  };

  /**
   * Decides one value through the review route: accepts the candidate acceptRowId names, and keeps
   * the approved value where the target names none. The row then says "Accepted" or "Kept the
   * current value" for NOTICE_MS, with no Undo.
   */
  function markReviewed(
    target: FieldWriteTarget,
    liveRowId: number | null = target.scope.liveRowId
  ) {
    const { definition, taxYear, scope, acceptRowId, recordId } = target;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    send(
      definition,
      () =>
        review(
          clientId,
          reviewItemsOf([{ ...target, scope: { ...scope, liveRowId } }]),
          shownEntityId
        ),
      (answer: ReviewTaxProfileFieldsResponse) => {
        const result = answer.results[0];
        if (result && !result.ok) {
          // Only a 409 carries the value as it stands, which is what "Keep theirs" and "Overwrite"
          // choose between. An item the api could make nothing of shows its own sentence instead.
          if (result.current) {
            const { current } = result;
            offerConflictChoice(definition, current, (currentRowId) =>
              markReviewed(target, currentRowId)
            );
          } else {
            alertErrorMessage(result.error);
          }
          return;
        }
        showNotice(
          noticeKey(definition.key, taxYear, recordId ?? null),
          acceptRowId != null ? ACCEPTED_NOTICE : KEPT_NOTICE
        );
      },
      (currentRowId) => markReviewed(target, currentRowId)
    );
  }

  /**
   * Accepts many values in one request: the accept-all button's values, each target naming its
   * candidate in acceptRowId.
   *
   * Expects the write targets of the values to accept. It raises no row notice and offers no Undo;
   * one toast says how many were accepted.
   *
   * The api answers one result per item whatever the others did, so the two groups of refusals are
   * read off the results rather than off whether the request succeeded. The values somebody else
   * changed while the page was open are named together in one toast and are left as they are; an
   * item the api could make nothing of gets its own toast carrying the api's sentence, which is not
   * about anybody having changed anything.
   */
  const markManyReviewed = async (targets: FieldWriteTarget[]) => {
    if (targets.length === 0) return;
    const items = reviewItemsOf(targets);
    writesInFlight.add(1);
    try {
      const answer = await review(clientId, items, shownEntityId);
      const refused = answer.results.filter((result) => !result.ok);
      const changedBySomebodyElse = refused.filter(
        (result) => !result.ok && result.status === 409
      );
      const otherwiseRefused = refused.filter(
        (result) => !result.ok && result.status !== 409
      );
      const marked = answer.results.length - refused.length;
      if (marked > 0) alertMessageSuccess(acceptedCountNotice(marked));
      if (changedBySomebodyElse.length > 0) {
        alertErrorMessage(
          bulkConflictSentence(
            changedBySomebodyElse.map((result) => labelOf(result.key))
          )
        );
      }
      otherwiseRefused.forEach((result) => {
        if (!result.ok)
          alertErrorMessage(`${labelOf(result.key)}: ${result.error}`);
      });
    } catch (error) {
      alertErrorMessage(
        (error as Error).message || 'The values could not be accepted'
      );
    } finally {
      writesInFlight.add(-1);
    }
  };

  /**
   * The Confirm review button of one row of the owners, officers, subsidiaries or state
   * registrations tables, and of a document's summary screen (ReviewModal): writes every choice
   * made in the row's cells, or in the summary's waiting lines.
   *
   * Expects `decided`, one target per cell with a candidate accepted (acceptRowId set) or the
   * approved value kept (acceptRowId left out), `typed`, the cells a person typed a value into, and
   * optionally `entityId`, the legal entity every value is on (ReviewModal passes the one its
   * document is summarized for); left out or null, the legal entity the page is showing.
   *
   * Sends the `decided` cells as the items of one request to the review route, then each typed cell
   * through the edit route (PUT), which closes that cell's candidates the page showed as the review
   * route would. A typed value cannot be an item of the review route, which only accepts a stored
   * row or keeps the approved one. Raises no toast and no row notice.
   *
   * Neither request refreshes anything itself ({ refresh: false }): the profile, the documents list
   * and every document summary are read again once, after the last request, whether or not a
   * request failed part-way, so the page shows what was saved. Before, each request refreshed all
   * of them, once per typed cell.
   *
   * Returns the cells the api refused, each with the api's sentence, so the row can list them: an
   * item of the review request that came back not ok, a PUT that was refused, or, when the review
   * request itself failed, every cell of it with the error's message.
   */
  const reviewListRow = async (
    decided: FieldWriteTarget[],
    typed: TypedCellWrite[],
    entityId: number | null = null
  ): Promise<RowWriteFailure[]> => {
    const failures: RowWriteFailure[] = [];
    const rowEntityId = entityId ?? shownEntityId;
    const entity = rowEntityId != null ? { entityId: rowEntityId } : {};
    /** Where a refusal is: the target's key, row and tax year. */
    const placeOf = (target: FieldWriteTarget) => ({
      key: target.definition.key,
      ...recordIdOf(target.recordId),
      taxYear: target.taxYear,
    });
    writesInFlight.add(1);
    try {
      if (decided.length > 0) {
        try {
          const answer = await review(
            clientId,
            reviewItemsOf(decided),
            rowEntityId,
            { refresh: false }
          );
          answer.results.forEach((result) => {
            if (!result.ok)
              failures.push({
                key: result.key,
                ...recordIdOf(result.recordId),
                taxYear: result.taxYear ?? null,
                error: result.error,
              });
          });
        } catch (error) {
          const message =
            (error as Error).message || 'The values could not be saved';
          decided.forEach((target) =>
            failures.push({ ...placeOf(target), error: message })
          );
        }
      }
      // One after the other, so each PUT reads the profile the one before it left.
      // eslint-disable-next-line no-restricted-syntax
      for (const { target, value } of typed) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await editField(
            clientId,
            target.definition.key,
            {
              ...target.scope,
              ...entity,
              taxYear: target.taxYear,
              ...recordIdOf(target.recordId),
              value,
            },
            { refresh: false }
          );
        } catch (error) {
          failures.push({
            ...placeOf(target),
            error: (error as Error).message || 'The value could not be saved',
          });
        }
      }
      // A refresh that fails leaves SWR holding the error, which the page shows as its line about
      // a failed refresh; it is not a refusal of any cell, so it is not added to `failures`.
      await refreshAfterFieldWrite(clientId).catch(() => undefined);
    } finally {
      writesInFlight.add(-1);
    }
    return failures;
  };

  /**
   * The Remove owner / Remove officer button of the person window: deletes the k1_partners or
   * entity_officers row `recordId` names, on the legal entity the page is showing, with every value
   * on file for it. useTaxProfile's removePerson then reads the profile and the documents again.
   *
   * Returns null once the api has removed the row, and the api's sentence when it refused, which
   * the window shows under its buttons. Raises no toast and no row notice: the row is gone, and
   * there is nothing to undo.
   */
  const removeListRow = async (
    table: TaxProfilePersonTable,
    recordId: number
  ): Promise<string | null> => {
    writesInFlight.add(1);
    try {
      await removePerson(clientId, table, recordId, shownEntityId);
      return null;
    } catch (error) {
      return (error as Error).message || REMOVE_PERSON_WORDS.failed;
    } finally {
      writesInFlight.add(-1);
    }
  };

  return {
    notices,
    isWriting,
    editValue,
    applyStoredRow,
    markReviewed,
    markManyReviewed,
    reviewListRow,
    removeListRow,
  };
}
